import { ChangeDetectionStrategy, Component, computed, inject, OnInit, output, signal } from '@angular/core';
import { take } from 'rxjs';
import { CreateScreeningService } from '@/app/pages/screening-brief-create/services/create-screening/create-screening.service';
import { ScreeningBriefAiService } from '../../services/screening-brief-ai.service';
import { ProductService } from '@/app/services/product/product.service';
import { CurrentUserService } from '@/app/common/current-user/current-user.service';
import { UgcProductResponseDto } from '@/app/common/general-types/products.dto';
import { ImageService } from '@/app/services/image/image.service';
import { BbAiDraftingLoader, SCREENING_AI_DRAFT_PHRASES } from '@/app/pages/create-brief-select/ui/bb-ai-drafting-loader/bb-ai-drafting-loader';
import { BriefBasics } from '../../sections/brief-basics/brief-basics';
import { BriefRequirements } from '../../sections/brief-requirements/brief-requirements';
import { ProductSelect } from '../../sections/product-select/product-select';
import { CreatorSelectMethod } from '../../sections/creator-select-method/creator-select-method';
import { ScreeningWindow } from '../../sections/screening-window/screening-window';

type Stage = 'intake' | 'generating' | 'review';

@Component({
  selector: 'sbs-ai-first-screening',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [BbAiDraftingLoader, BriefBasics, BriefRequirements, ProductSelect, CreatorSelectMethod, ScreeningWindow],
  templateUrl: './ai-first-screening.html',
  styleUrl: './ai-first-screening.scss',
})
export class AiFirstScreening implements OnInit {
  readonly service = inject(CreateScreeningService);
  readonly screeningAi = inject(ScreeningBriefAiService);
  private readonly productService = inject(ProductService);
  private readonly currentUserService = inject(CurrentUserService);
  public readonly imageService = inject(ImageService);

  readonly launch = output<void>();
  readonly startOver = output<void>();

  readonly stage = signal<Stage>('intake');
  readonly prompt = signal<string>('');
  readonly products = signal<UgcProductResponseDto[]>([]);
  readonly aiBusy = signal<boolean>(false);
  readonly canLaunch = computed(() => !!this.service.selectedProduct());

  readonly screeningPhrases = SCREENING_AI_DRAFT_PHRASES;

  readonly examples: { name: string; description: string; prompt: string }[] = [
    { name: 'On-brand advocates', description: 'Creators already posting on-brand content.', prompt: 'Find creators whose past content already aligns with our brand tone and product category.' },
    { name: 'Authentic users', description: 'Genuine past mentions of the product.', prompt: 'Screen for creators who have authentically featured or mentioned this product (or its category) in previous posts.' },
    { name: 'Hands-on demos', description: 'Real product demos in past content.', prompt: 'Look for past content showing hands-on demos of similar products, with no paid competitor promotions.' },
    { name: 'Family-friendly', description: 'Consistently safe, on-brand tone.', prompt: 'Screen for family-friendly creators with a consistent, brand-safe tone across their content history.' },
  ];

  readonly reviewSections = [
    { id: 'brief', title: 'Brief basics' },
    { id: 'product', title: 'Product' },
    { id: 'requirements', title: 'Screening requirements' },
    { id: 'timeframe', title: 'Screening window' },
    { id: 'creators', title: 'Creators to screen' },
  ] as const;

  ngOnInit(): void {
    // Resume into review if a draft already exists (e.g. returning to this flow).
    if (this.service.basicInfoModel().briefTitle && this.service.briefTextHtml()) {
      this.stage.set('review');
    }
    const brandId = this.currentUserService.focussedBrand?.id;
    if (brandId) {
      this.productService
        .getbusinessProducts(brandId)
        .pipe(take(1))
        .subscribe({ next: (response) => this.products.set((response as UgcProductResponseDto[]) ?? []) });
    }
  }

  async generate(): Promise<void> {
    if (this.aiBusy()) {
      return;
    }
    // Fall back to the first product if none chosen (mirrors the create-brief AI flow).
    if (!this.service.selectedProduct() && this.products().length) {
      this.service.selectedProduct.set(this.products()[0]);
    }

    this.aiBusy.set(true);
    this.stage.set('generating');

    const angle = this.prompt().trim() || 'Draft a strong content-screening brief considering this product and brand.';
    await this.screeningAi.draftScreeningBrief(angle);

    this.aiBusy.set(false);
    this.stage.set('review');
  }

  onStartOver(): void {
    this.prompt.set('');
    this.stage.set('intake');
    this.startOver.emit();
  }

  async regenerate(): Promise<void> {
    if (this.aiBusy()) {
      return;
    }
    this.aiBusy.set(true);
    try {
      await this.screeningAi.draftScreeningBrief(
        this.prompt().trim() || 'Regenerate the screening requirements for this brief.',
      );
    } finally {
      this.aiBusy.set(false);
    }
  }
}
